import {
    forwardRef,
    ReactElement,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { TabsProps } from './props'
import { Indicator, StyledNavigationTabs, Tab } from './styles'
import { Text } from '..'
import useIsomorphicLayoutEffect from '../../Hooks/useIsomorphicLayoutEffect'
import { Grid } from '../../Molecules'

const NavigationTabs = forwardRef<HTMLElement, TabsProps>(
    ({ activeTab, tabs, ...props }, ref): ReactElement => {
        const wrapperRef = useRef<HTMLDivElement>(null)

        const indexTab = useMemo(
            () => tabs.findIndex((tab) => tab.value === activeTab),
            [activeTab, tabs]
        )

        const [currentTab, setCurrentTab] = useState(
            indexTab > -1 ? indexTab : 0
        )
        const [lineStyles, setLineStyles] = useState({ left: 0, width: 0 })

        useEffect(() => {
            if (indexTab > -1) setCurrentTab(indexTab)
        }, [indexTab])

        useIsomorphicLayoutEffect(() => {
            const wrapperDiv = wrapperRef.current
            if (wrapperDiv) {
                const current =
                    wrapperDiv.children[currentTab].getBoundingClientRect()
                const first = wrapperDiv.children[0].getBoundingClientRect()

                setLineStyles({
                    left: current.x - first.x,
                    width: current.width,
                })
            }
        }, [currentTab])

        const tabsItems = useMemo(
            () =>
                tabs.map((tab, key) => (
                    <Tab
                        active={key === currentTab}
                        key={key}
                        onClick={() => {
                            setCurrentTab(key)
                            tab.onClickTab?.(tab.value)
                        }}
                    >
                        {tab.icon}
                        {tab.label && (
                            <Text
                                size={{ '@mobile': 'xs', '@desktop': 's' }}
                                ellipsis
                            >
                                {tab.label}
                            </Text>
                        )}
                    </Tab>
                )),
            [tabs, currentTab]
        )

        return (
            <StyledNavigationTabs {...props} ref={ref}>
                <Grid
                    autoFlow="column"
                    css={{
                        gridAutoColumns: 'minmax(0, 1fr)',
                        position: 'relative',

                        '&::after': {
                            bgc: '$neutral300',
                            content: `""`,
                            h: 1,
                            left: 0,
                            position: 'absolute',
                            right: 0,
                        },
                    }}
                    gap={4}
                    ref={wrapperRef}
                >
                    {tabsItems}
                    <Indicator css={lineStyles} />
                </Grid>
            </StyledNavigationTabs>
        )
    }
)

NavigationTabs.displayName = 'NavigationTabs'

export default NavigationTabs

export { StyledNavigationTabs }
